import type { ResumenEjecucionDto } from '@/services/sysadmin.service';
import { AlertTriangle, CheckCircle2, ChevronDown } from 'lucide-react';
import { useState } from 'react';

function duracion(ms: number): string {
  const seg = Math.round(ms / 1000);
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = seg % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}

function usd(valor: number): string {
  // Una sola acta cuesta centavos: con 2 decimales se vería "US$0.00" y parecería gratis.
  return `US$${valor.toFixed(valor > 0 && valor < 0.01 ? 4 : 2)}`;
}

function miles(n: number): string {
  return Math.round(n).toLocaleString('es-CO');
}

function Metrica({ label, valor, detalle }: { label: string; valor: string; detalle?: string }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">{valor}</dd>
      {detalle && <dd className="text-[11px] text-gray-500">{detalle}</dd>}
    </div>
  );
}

/** Métricas de la última corrida: tiempo, actas, consumo de IA y costo. */
export function ResumenRegeneracion({ resumen }: { resumen: ResumenEjecucionDto }) {
  const [verErrores, setVerErrores] = useState(false);

  const filtros = resumen.filtros ?? {};
  const filtrosTexto = [
    filtros.empresaIds?.length ? `${filtros.empresaIds.length} empresa(s) filtrada(s)` : 'todas las empresas',
    filtros.limit ? `límite ${filtros.limit}` : null,
    filtros.force ? 'reprocesando ya regeneradas' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
        <h4 className="text-sm font-semibold text-gray-900">Resumen de la última ejecución</h4>
        <span className="ml-auto text-[11px] text-gray-400">{new Date(resumen.finalizadoEn).toLocaleString('es-CO')}</span>
      </div>

      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Metrica label="Tiempo" valor={duracion(resumen.duracionMs)} detalle={`${duracion(resumen.msPromedioPorActa)} por acta`} />
        <Metrica
          label="Actas regeneradas"
          valor={String(resumen.regeneradas)}
          detalle={`de ${resumen.totalElegibles} · ${resumen.empresas} empresa(s)`}
        />
        <Metrica
          label="Tokens de IA"
          valor={miles(resumen.tokens.totalTokens)}
          detalle={`${resumen.tokens.llamadas} llamada(s) a Gemini`}
        />
        <Metrica label="Costo" valor={usd(resumen.costoUsd)} detalle={`≈ ${miles(resumen.costoCop)} COP`} />
      </dl>

      <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-1.5 border-t border-gray-100 pt-3 text-xs text-gray-600 sm:grid-cols-2">
        <div className="flex justify-between gap-3">
          <span className="text-gray-500">Con IA</span>
          <span className="tabular-nums">
            {resumen.conIA} acta(s) · {duracion(resumen.msPromedioConIA)} c/u · {usd(resumen.costoUsdPromedioPorActaConIA)} c/u
          </span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-gray-500">Reusando texto (sin costo)</span>
          <span className="tabular-nums">
            {resumen.sinIA} acta(s) · {duracion(resumen.msPromedioSinIA)} c/u
          </span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-gray-500">Ya estaban al día</span>
          <span className="tabular-nums">{resumen.saltadas} acta(s)</span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-gray-500">Tokens (entrada / facturables)</span>
          <span className="tabular-nums">
            {miles(resumen.tokens.inputTokens)} / {miles(resumen.tokens.outputTokens)}
          </span>
        </div>
        <div className="flex justify-between gap-3 sm:col-span-2">
          <span className="text-gray-500">Filtros usados</span>
          <span className="text-right">{filtrosTexto}</span>
        </div>
        {resumen.viejosSinRenombrar > 0 && (
          <div className="flex justify-between gap-3 sm:col-span-2">
            <span className="text-amber-700">Documentos viejos sin renombrar</span>
            <span className="text-right tabular-nums text-amber-700">
              {resumen.viejosSinRenombrar} — el acta sí se regeneró; el documento anterior quedó con su nombre original
            </span>
          </div>
        )}
        {resumen.restantes > 0 && (
          <div className="flex justify-between gap-3 sm:col-span-2">
            <span className="text-gray-500">Quedan sin procesar</span>
            <span className="text-right tabular-nums">
              {resumen.restantes} acta(s) — estimado {duracion(resumen.proyeccionMs)} y {usd(resumen.proyeccionCostoUsd)}
            </span>
          </div>
        )}
      </div>

      {resumen.fallidas > 0 && (
        <div className="mt-4 rounded-lg bg-red-50 px-3 py-2.5">
          <button
            type="button"
            onClick={() => setVerErrores((v) => !v)}
            className="flex w-full items-center gap-2 text-left text-sm font-medium text-red-700"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {resumen.fallidas} acta(s) fallaron
            <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${verErrores ? 'rotate-180' : ''}`} />
          </button>
          {verErrores && (
            <ul className="mt-2 space-y-1.5 border-t border-red-200 pt-2 text-xs text-red-800">
              {resumen.errores.map((e) => (
                <li key={e.asesoriaId}>
                  <span className="font-mono font-semibold">Asesoría {e.asesoriaId}:</span> {e.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
